import { describeFileRead } from '../fileReadability';
import { archiveAttachments, createPendingRow, isUnreadableArchiveChild, isZipFile, replaceRow, spliceRow, withProcessingStatus, withProgress, withUploadError, withUploaded } from './rows';
/** Builds the zero-byte File a child row displays — the real bytes live in tesoro, never
 *  re-downloaded just to render a row. Size is patched in for an honest "430 KB" caption. */ function childFile(filename, mimeType, sizeBytes) {
    const file = new File([], filename, {
        type: mimeType || 'application/octet-stream'
    });
    if (sizeBytes > 0) {
        Object.defineProperty(file, 'size', {
            value: sizeBytes
        });
    }
    return file;
}
/**
 * Upload an archive row's bytes with `autoProcess: false` — reverie/baltigo must never see
 * the ZIP itself, only its expanded children. Mirrors the plain-file branch in `submitBatch`,
 * but shares this path with `retry.ts` so an archive upload-retry still goes on to expand.
 */ export async function uploadArchiveBytes(rows, row, context, transport, onRowsChange) {
    let current = replaceRow(rows, row.id, (r)=>({
            ...r,
            status: 'uploading',
            progress: 0,
            isArchive: true,
            statusLabel: `Uploading ${r.file.name}…`
        }));
    onRowsChange(current);
    try {
        const result = await transport(row.file, {
            tenantId: context.tenantId,
            ownerUserId: context.ownerUserId,
            chatId: context.chatId,
            autoProcess: false,
            onProgress: (percent)=>{
                current = replaceRow(current, row.id, (r)=>({
                        ...withProgress(r, percent),
                        statusLabel: `Uploading ${r.file.name}…`
                    }));
                onRowsChange(current);
            }
        });
        return {
            ok: true,
            rows: current,
            artifactId: result.artifactId
        };
    } catch (err) {
        current = replaceRow(current, row.id, (r)=>{
            const failed = withUploadError(r, err);
            return {
                ...failed,
                isArchive: true,
                statusLabel: failed.rejection ? undefined : `Failed to upload ${r.file.name}. Retry or remove, then send.`
            };
        });
        onRowsChange(current);
        return {
            ok: false,
            rows: current
        };
    }
}
/**
 * Expand an already-uploaded archive artifact and splice its children into the row's own
 * position. Three outcomes: the RPC itself fails, it succeeds but nothing came back usable
 * (every member skipped), or it succeeds with at least one child — each leaves the archive
 * row (or its replacement children) in a state `statusLabel` can show truthfully.
 */ export async function expandArchiveRow(rows, rowId, archiveName, artifactId, useDeepProcessing, outputFormat, expandArchive, onRowsChange) {
    let current = replaceRow(rows, rowId, (r)=>({
            ...r,
            status: 'expanding',
            attachmentId: artifactId,
            isArchive: true,
            statusLabel: `Unpacking ${archiveName}…`
        }));
    onRowsChange(current);
    let response;
    try {
        response = await expandArchive(artifactId, {
            autoProcess: true,
            useDeepProcessing,
            outputFormat
        });
    } catch  {
        current = replaceRow(current, rowId, (r)=>({
                ...r,
                status: 'error',
                statusLabel: `Failed to unpack ${archiveName}. Retry or remove, then send.`
            }));
        onRowsChange(current);
        return {
            ok: false,
            rows: current,
            skipped: []
        };
    }
    const skipped = response.skipped.map((member)=>({
            archiveName,
            path: member.path,
            reason: member.reason
        }));
    if (response.children.length === 0) {
        const uniqueReasons = [
            ...new Set(response.skipped.map((member)=>member.reason))
        ].join(', ');
        current = replaceRow(current, rowId, (r)=>({
                ...r,
                status: 'error',
                statusLabel: `No usable files in ${archiveName} — ${response.skipped.length} member${response.skipped.length === 1 ? '' : 's'} skipped: ${uniqueReasons}`
            }));
        onRowsChange(current);
        return {
            ok: false,
            rows: current,
            skipped
        };
    }
    const childRows = response.children.map((child)=>{
        const ability = describeFileRead(child.filename, child.mimeType);
        const needsJob = ability.kind === 'convert';
        return createPendingRow(childFile(child.filename, child.mimeType, child.sizeBytes), {
            status: 'uploaded',
            attachmentId: child.artifactId,
            jobId: child.processingJobId || null,
            processingStatus: child.processingJobId ? 'processing' : needsJob ? 'error' : 'ready',
            statusLabel: child.processingJobId ? undefined : needsJob ? 'Processing could not start. Retry or remove this file.' : ability.message,
            sourceArchiveName: archiveName,
            sourceArchiveId: artifactId,
            progress: 100
        });
    });
    current = spliceRow(current, rowId, childRows);
    onRowsChange(current);
    const attachments = response.children.map((child)=>({
            id: child.artifactId,
            name: child.filename,
            contentType: child.mimeType,
            jobId: child.processingJobId || undefined
        }));
    return {
        ok: true,
        rows: current,
        attachments,
        skipped
    };
}
/**
 * Upload every row that doesn't already hold an attachmentId (exactly once
 * each), then — if any upload kicked off a processing job — poll until every
 * job reaches a terminal state. Every intermediate state is reported via
 * `onRowsChange` with a freshly built array; nothing already handed out is
 * ever mutated in place.
 *
 * A row whose file is a ZIP is never sent to reverie/baltigo as-is: it's
 * uploaded with autoProcess=false, expanded via ExpandArchiveArtifact, and
 * spliced out in favor of its children — the archive is never part of
 * `attachments`, only the children a chat request can actually cite.
 */ export async function submitBatch(rows, context, deps, onRowsChange) {
    if (rows.some((row)=>row.sourceDownloadPending)) {
        return {
            ok: false,
            reason: 'upload-failed',
            rows,
            skipped: []
        };
    }
    const sleep = deps.sleep ?? ((ms)=>new Promise((resolve)=>setTimeout(resolve, ms)));
    const now = deps.now ?? (()=>Date.now());
    const pollIntervalMs = deps.pollIntervalMs ?? 2000;
    const maxWaitMs = deps.maxWaitMs ?? 300000;
    let current = rows;
    const attachments = [];
    const skipped = [];
    for (const row of rows){
        if (row.isArchive && row.attachmentId) {
            const expanded = await expandArchiveRow(current, row.id, row.file.name, row.attachmentId, row.useDeepProcessing, row.outputFormat, deps.expandArchive, onRowsChange);
            current = expanded.rows;
            skipped.push(...expanded.skipped);
            if (expanded.ok) attachments.push(...expanded.attachments);
            continue;
        }
        if (row.attachmentId && row.processingStatus === 'error' && !row.jobId) {
            current = replaceRow(current, row.id, (r)=>({
                    ...r,
                    status: 'error'
                }));
            onRowsChange(current);
            continue;
        }
        // Already uploaded (a prior submit, or a restored localStorage row) —
        // reuse the cached identifiers. Re-uploading would replace the real
        // artifact with an empty one and orphan the original bytes.
        if (row.status === 'uploaded' && row.attachmentId) {
            attachments.push({
                id: row.attachmentId,
                name: row.file.name,
                contentType: row.contentType ?? row.file.type,
                jobId: row.jobId ?? undefined
            });
            continue;
        }
        if (isZipFile(row.file)) {
            const uploadOutcome = await uploadArchiveBytes(current, row, context, deps.transport, onRowsChange);
            current = uploadOutcome.rows;
            if (!uploadOutcome.ok) continue;
            const expandOutcome = await expandArchiveRow(current, row.id, row.file.name, uploadOutcome.artifactId, row.useDeepProcessing, row.outputFormat, deps.expandArchive, onRowsChange);
            current = expandOutcome.rows;
            skipped.push(...expandOutcome.skipped);
            if (!expandOutcome.ok) continue;
            attachments.push(...expandOutcome.attachments);
            continue;
        }
        current = replaceRow(current, row.id, (r)=>({
                ...r,
                status: 'uploading',
                progress: 0
            }));
        onRowsChange(current);
        try {
            const result = await deps.transport(row.file, {
                tenantId: context.tenantId,
                ownerUserId: context.ownerUserId,
                chatId: context.chatId,
                autoProcess: describeFileRead(row.file.name, row.file.type).kind === 'convert',
                useDeepProcessing: row.useDeepProcessing,
                outputFormat: row.outputFormat,
                onProgress: (percent)=>{
                    current = replaceRow(current, row.id, (r)=>withProgress(r, percent));
                    onRowsChange(current);
                }
            });
            current = replaceRow(current, row.id, (r)=>withUploaded(r, result));
            onRowsChange(current);
            attachments.push({
                id: result.artifactId,
                name: result.filename,
                contentType: result.mimeType,
                jobId: result.jobId
            });
        } catch (err) {
            current = replaceRow(current, row.id, (r)=>withUploadError(r, err));
            onRowsChange(current);
        }
    }
    if (current.some((r)=>r.status === 'error')) {
        return {
            ok: false,
            reason: 'upload-failed',
            rows: current,
            skipped
        };
    }
    const documentsWithJobs = attachments.filter((a)=>a.jobId);
    if (documentsWithJobs.length > 0) {
        deps.onPollStart?.();
        const startTime = now();
        while(now() - startTime < maxWaitMs){
            const statuses = await Promise.all(documentsWithJobs.map(async (doc)=>{
                try {
                    const resp = await deps.fetchJobStatus(doc.jobId);
                    return {
                        jobId: doc.jobId,
                        status: resp.status
                    };
                } catch  {
                    return {
                        jobId: doc.jobId,
                        status: 'error'
                    };
                }
            }));
            const allComplete = statuses.every((s)=>s.status === 'completed' || s.status === 'failed' || s.status === 'cancelled');
            // Match by jobId, not filename — two rows can share a filename.
            current = current.map((row)=>{
                if (!row.jobId) return row;
                const match = statuses.find((s)=>s.jobId === row.jobId);
                if (!match) return row;
                if (match.status === 'completed') return withProcessingStatus(row, 'ready');
                if (match.status === 'failed' || match.status === 'cancelled') return withProcessingStatus(row, 'error');
                if (match.status === 'running' || match.status === 'pending') return withProcessingStatus(row, 'processing');
                return row;
            });
            onRowsChange(current);
            if (allComplete) break;
            await sleep(pollIntervalMs);
        }
        // An unpacked zip member chat cannot read is still attached and fileable.
        const anyFailed = documentsWithJobs.some((doc)=>{
            const row = current.find((r)=>r.jobId === doc.jobId);
            return row?.processingStatus === 'error' && !(row && isUnreadableArchiveChild(row));
        });
        const anyStillProcessing = documentsWithJobs.some((doc)=>{
            const row = current.find((r)=>r.jobId === doc.jobId);
            return row?.processingStatus === 'processing' || row?.processingStatus === 'queued';
        });
        if (anyFailed) return {
            ok: false,
            reason: 'processing-failed',
            rows: current,
            skipped
        };
        if (anyStillProcessing) return {
            ok: false,
            reason: 'processing-timeout',
            rows: current,
            skipped
        };
    }
    for (const archive of archiveAttachments(current)){
        if (!attachments.some((a)=>a.id === archive.id)) attachments.push(archive);
    }
    return {
        ok: true,
        rows: current,
        attachments,
        skipped
    };
}
